import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { EmptyState } from './ui/EmptyState';
import { RunningState } from './ui/RunningState';
import {
  DEFAULT_ZOOM_INDEX,
  VIEWER_MIN_WIDTH_PX,
  ZOOM_LEVELS,
  clampPage,
  computeCssScale,
  findHighlightPage,
  highlightBoxStyle,
  highlightsOnPage,
} from './documentViewerGeometry';
import type { ViewerHighlight } from './documentViewerGeometry';

/**
 * DocumentViewer: renders one PDF page with pdfjs and overlays field highlights.
 *
 * Generic on purpose (used by Extract and Review modes): the owner supplies the
 * document source, the highlights (id + page + normalised bounding box) and the
 * selected id. Selecting a highlight calls `onSelect(id)`; changing
 * `selectedId` highlights that region and jumps to its page.
 *
 * The page scrolls inside the viewer, the viewer never drops below 320px, and
 * the page is fitted to the available width so 1180px layouts do not overflow.
 * Light theme only.
 */

export type DocumentSource =
  | { data: ArrayBuffer | Uint8Array; url?: undefined }
  | { url: string; data?: undefined };

export interface DocumentViewerProps {
  /** PDF bytes or URL. Memoise it: a new object reloads the document. */
  source: DocumentSource | null;
  /** Controlled 1-based page. Omit to let the viewer manage it. */
  page?: number;
  onPageChange?: (page: number) => void;
  highlights?: ReadonlyArray<ViewerHighlight>;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  /** Shown while the owner is still fetching the bytes. */
  loading?: boolean;
  /** Owner-side load error (e.g. S3 fetch failed). */
  error?: string | null;
  className?: string;
}

const HIGHLIGHT_BASE =
  'absolute cursor-pointer rounded-[3px] border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5A21C9] focus-visible:ring-offset-1';
const HIGHLIGHT_IDLE =
  'border-[#834DFB]/45 bg-[#834DFB]/[0.08] hover:bg-[#834DFB]/[0.16]';
const HIGHLIGHT_SELECTED = 'z-10 border-[#834DFB] bg-[#834DFB]/[0.18]';
const BADGE_BASE =
  'pointer-events-none absolute -top-1 right-full mr-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border px-1 text-[10px] font-bold leading-none';
const BADGE_IDLE = 'border-[#834DFB]/60 bg-white text-[#5A21C9]';
const BADGE_SELECTED = 'border-[#834DFB] bg-[#834DFB] text-white';

export interface HighlightLayerProps {
  highlights: ReadonlyArray<ViewerHighlight>;
  page: number;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  /** Accessible name for a highlight; defaults to its id. */
  getLabel?: (highlight: ViewerHighlight) => string;
}

/** Clickable boxes for one page, positioned in percent of the page box. */
export const HighlightLayer: React.FC<HighlightLayerProps> = ({
  highlights,
  page,
  selectedId,
  onSelect,
  getLabel,
}) => (
  <>
    {highlightsOnPage(highlights, page).map((h) => {
      const selected = h.id === selectedId;
      return (
        <button
          key={h.id}
          type="button"
          data-highlight-id={h.id}
          aria-label={getLabel ? getLabel(h) : `Field ${h.id}`}
          aria-pressed={selected}
          onClick={() => onSelect?.(h.id)}
          className={`${HIGHLIGHT_BASE} ${selected ? HIGHLIGHT_SELECTED : HIGHLIGHT_IDLE}`}
          style={highlightBoxStyle(h.boundingBox)}
        >
          {h.badge ? (
            <span
              data-testid="highlight-badge"
              aria-hidden="true"
              className={`${BADGE_BASE} ${selected ? BADGE_SELECTED : BADGE_IDLE}`}
            >
              {h.badge}
            </span>
          ) : null}
        </button>
      );
    })}
  </>
);

const iconButton =
  'inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-600 hover:bg-[#F5F3FF] hover:text-[#5A21C9] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-600';

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  source,
  page,
  onPageChange,
  highlights = [],
  selectedId = null,
  onSelect,
  loading = false,
  error = null,
  className = '',
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [internalPage, setInternalPage] = useState(1);
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  const [containerWidth, setContainerWidth] = useState(0);
  const [pageBox, setPageBox] = useState<{ width: number; height: number } | null>(null);

  const totalPages = pdf?.numPages ?? 0;
  const currentPage = clampPage(page ?? internalPage, totalPages);

  const setPage = (next: number) => {
    const clamped = clampPage(next, totalPages);
    if (page === undefined) setInternalPage(clamped);
    onPageChange?.(clamped);
  };

  // ── Load the document ─────────────────────────────────────────────────────
  useEffect(() => {
    setPdf(null);
    setPageBox(null);
    setLoadError(null);
    if (!source) return;

    let cancelled = false;
    let task: { promise: Promise<PDFDocumentProxy>; destroy: () => Promise<void> } | null = null;
    setParsing(true);

    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url
        ).toString();
        // pdfjs transfers the buffer to its worker, so hand it a copy.
        const params =
          source.url !== undefined
            ? { url: source.url }
            : {
                data:
                  source.data instanceof Uint8Array
                    ? source.data.slice()
                    : new Uint8Array(source.data.slice(0)),
              };
        task = pdfjs.getDocument(params);
        const doc = await task.promise;
        if (cancelled) {
          void doc.destroy();
          return;
        }
        setPdf(doc);
      } catch (e) {
        if (!cancelled) {
          console.error('[DocumentViewer] PDF load failed:', e);
          setLoadError('The document could not be opened. Reload the page or upload the file again.');
        }
      } finally {
        if (!cancelled) setParsing(false);
      }
    })();

    return () => {
      cancelled = true;
      void task?.destroy();
    };
  }, [source]);

  // ── Track available width for fit-to-width ────────────────────────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [pdf]);

  // ── Jump to the selected highlight's page ─────────────────────────────────
  const selectedPage = useMemo(
    () => findHighlightPage(highlights, selectedId),
    [highlights, selectedId]
  );
  useEffect(() => {
    if (selectedPage !== null && totalPages > 0 && selectedPage !== currentPage) {
      setPage(selectedPage);
    }
    // Only react to selection changes, not to manual page turns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedPage, totalPages]);

  // ── Render the current page ───────────────────────────────────────────────
  const zoom = ZOOM_LEVELS[zoomIndex];
  useEffect(() => {
    if (!pdf || totalPages < 1) return;
    let cancelled = false;
    let renderTask: RenderTask | null = null;

    (async () => {
      try {
        const pdfPage = await pdf.getPage(currentPage);
        if (cancelled) return;
        const base = pdfPage.getViewport({ scale: 1 });
        const fit = Math.min(computeCssScale(containerWidth, base.width, 1), 1.5);
        const cssScale = fit * zoom;
        const cssW = Math.floor(base.width * cssScale);
        const cssH = Math.floor(base.height * cssScale);
        setPageBox({ width: cssW, height: cssH });

        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
        const viewport = pdfPage.getViewport({ scale: cssScale * dpr });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        renderTask = pdfPage.render({ canvasContext: ctx, viewport });
        await renderTask.promise;
      } catch (e) {
        const name = (e as { name?: string } | null)?.name;
        if (!cancelled && name !== 'RenderingCancelledException') {
          console.error('[DocumentViewer] Page render failed:', e);
          setLoadError('This page could not be drawn. Try another page or reload.');
        }
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, totalPages, currentPage, containerWidth, zoom]);

  // ── Keep the selected highlight in view ───────────────────────────────────
  useEffect(() => {
    if (!selectedId || selectedPage !== currentPage || !pageBox) return;
    const el = Array.from(
      scrollRef.current?.querySelectorAll<HTMLElement>('[data-highlight-id]') ?? []
    ).find((node) => node.dataset.highlightId === selectedId);
    el?.scrollIntoView?.({ block: 'center', inline: 'nearest' });
  }, [selectedId, selectedPage, currentPage, pageBox]);

  const shownError = error ?? loadError;
  let body: React.ReactNode;
  if (shownError) {
    body = (
      <div className="m-auto w-full max-w-sm">
        <EmptyState title="Document unavailable" description={shownError} />
      </div>
    );
  } else if (loading || parsing) {
    body = (
      <div className="m-auto w-full max-w-sm">
        <RunningState title="Loading document" description="Fetching the pages. This takes a moment." />
      </div>
    );
  } else if (!source || !pdf) {
    body = (
      <div className="m-auto w-full max-w-sm">
        <EmptyState title="No document to show" description="Choose a document to see its pages here." />
      </div>
    );
  } else {
    body = (
      <div
        className="relative m-auto shrink-0 bg-white shadow-lg"
        style={pageBox ? { width: pageBox.width, height: pageBox.height } : undefined}
        data-testid="document-page"
      >
        <canvas ref={canvasRef} className="block h-full w-full" />
        <HighlightLayer
          highlights={highlights}
          page={currentPage}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      </div>
    );
  }

  const ready = !shownError && !!pdf;

  return (
    <section
      aria-label="Document viewer"
      className={`flex min-h-0 flex-1 flex-col bg-[#f7f6fb] ${className}`.trim()}
      style={{ minWidth: VIEWER_MIN_WIDTH_PX }}
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={iconButton}
            aria-label="Previous page"
            disabled={!ready || currentPage <= 1}
            onClick={() => setPage(currentPage - 1)}
          >
            <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
          </button>
          <span className="min-w-[56px] text-center text-xs text-gray-600" aria-live="polite">
            {ready ? `Page ${currentPage} of ${totalPages}` : 'No pages'}
          </span>
          <button
            type="button"
            className={iconButton}
            aria-label="Next page"
            disabled={!ready || currentPage >= totalPages}
            onClick={() => setPage(currentPage + 1)}
          >
            <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={iconButton}
            aria-label="Zoom out"
            disabled={!ready || zoomIndex <= 0}
            onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
          >
            <ZoomOut size={16} strokeWidth={2} aria-hidden="true" />
          </button>
          <span className="min-w-[40px] text-center text-xs text-gray-600">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            className={iconButton}
            aria-label="Zoom in"
            disabled={!ready || zoomIndex >= ZOOM_LEVELS.length - 1}
            onClick={() => setZoomIndex((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
          >
            <ZoomIn size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </div>
      <div ref={scrollRef} className="flex min-h-0 flex-1 overflow-auto p-4">
        {body}
      </div>
    </section>
  );
};

export default DocumentViewer;
